import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { CalendarIcon, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Slider } from '../components/ui/slider';

export default function AddTopic() {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [date, setDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [estimatedTime, setEstimatedTime] = useState(30);
  const [difficulty, setDifficulty] = useState(3);
  const [parentId, setParentId] = useState('');
  const [topics, setTopics] = useState([]);

  useEffect(() => {
    api.get('/topics').then(r => setTopics(r.data || [])).catch(() => {});
  }, []);

  const specialties = [
    'Cardiologia',
    'Pneumologia',
    'Gastroenterologia',
    'Nefrologia',
    'Neurologia',
    'Endocrinologia',
    'Hematologia',
    'Reumatologia',
    'Infectologia',
    'Oncologia',
    'Pediatria',
    'Ginecologia e Obstetrícia',
    'Cirurgia Geral',
    'Ortopedia',
    'Psiquiatria',
    'Outros'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim() || !specialty.trim()) {
      toast.error('Preencha o título e a especialidade do tema');
      return;
    }

    setLoading(true);
    try {
      const topicData = {
        title: title.trim(),
        specialty: specialty,
        scheduled_date: date.toISOString(),
        scheduled_time: scheduledTime || null,
        estimated_time_minutes: estimatedTime,
        difficulty_level: difficulty,
        parent_id: parentId === 'none' ? null : parentId || null
      };
      
      await api.post('/topics', topicData);
      toast.success('Tema adicionado com sucesso!');
      navigate('/topics');
    } catch (error) {
      console.error('Error creating topic:', error);
      toast.error('Erro ao adicionar tema');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container" data-testid="add-topic-page">
      <div className="max-w-md mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold tracking-tight mb-2">Novo Tema</h1>
          <p className="text-lg text-muted-foreground">Adicione um novo tema de estudo</p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Detalhes do Tema
            </CardTitle>
            <CardDescription>
              Organize seus estudos de forma eficiente
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="title">Título do Tema</Label>
                <Input
                  id="title"
                  type="text"
                  placeholder="Ex: Insuficiência Cardíaca Descompensada"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  data-testid="topic-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="specialty">Especialidade Médica</Label>
                <Select value={specialty} onValueChange={setSpecialty} required>
                  <SelectTrigger data-testid="specialty-select">
                    <SelectValue placeholder="Selecione uma especialidade" />
                  </SelectTrigger>
                  <SelectContent>
                    {specialties.map((spec) => (
                      <SelectItem key={spec} value={spec}>
                        {spec}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="time">Horário (opcional)</Label>
                <Input
                  id="time"
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  data-testid="topic-time-input"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400">Deixe vazio se não quiser definir horário</p>
              </div>

              <div className="space-y-2">
                <Label>Data de Estudo</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="date-picker-trigger"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {date ? format(date, "PPP", { locale: ptBR }) : <span>Selecione uma data</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={(newDate) => newDate && setDate(newDate)}
                      initialFocus
                      locale={ptBR}
                      data-testid="date-picker-calendar"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>Tempo Estimado: {estimatedTime} min</Label>
                <Slider value={[estimatedTime]} onValueChange={(v) => setEstimatedTime(v[0])} min={5} max={120} step={5} />
              </div>

              <div className="space-y-2">
                <Label>Dificuldade: {difficulty}/5</Label>
                <Slider value={[difficulty]} onValueChange={(v) => setDifficulty(v[0])} min={1} max={5} step={1} />
              </div>

              <div className="space-y-2">
                <Label>Tema Pai (opcional)</Label>
                <Select value={parentId} onValueChange={setParentId}>
                  <SelectTrigger><SelectValue placeholder="Nenhum (tema raiz)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum (tema raiz)</SelectItem>
                    {topics.map(t => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/topics')}
                  className="flex-1"
                  data-testid="cancel-button"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1"
                  data-testid="submit-button"
                >
                  {loading ? 'Salvando...' : 'Adicionar Tema'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
